import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  { value: "spam",                  label: "Spam" },
  { value: "harassment",            label: "Harcèlement" },
  { value: "inappropriate_content", label: "Contenu inapproprié" },
  { value: "fake_profile",          label: "Faux profil / Usurpation" },
  { value: "scam",                  label: "Arnaque" },
  { value: "intellectual_property", label: "Vol de droits d'auteur" },
  { value: "minor",                 label: "Mineur (-18 ans)" },
  { value: "violence",              label: "Violence / Discours haineux" },
  { value: "other",                 label: "Autre" },
];

/** Compact "Signaler" button + modal. Drop-in usage:
 *    <ReportButton targetType="creator" targetId={creator.user_id} />
 *    <ReportButton targetType="collab"  targetId={collab.id} variant="ghost" />
 */
export default function ReportButton({ targetType, targetId, variant = "ghost", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  if (!user) return null; // only show to authenticated users

  const submit = async () => {
    if (!reason) {
      toast.error("Choisissez une raison");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        description: description || null,
      });
      toast.success("Signalement envoyé. Merci.");
      setOpen(false);
      setReason("");
      setDescription("");
    } catch (e) {
      toast.error(e?.userMessage || "Échec du signalement");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant={variant}
        onClick={() => setOpen(true)}
        className={`text-xs text-[#8B8680] hover:text-[#B44F3F] inline-flex items-center gap-1 ${className}`}
        data-testid={`report-btn-${targetType}-${targetId}`}
      >
        <Flag className="w-3.5 h-3.5" />
        Signaler
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>Signaler ce contenu</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#666]">
              Votre signalement reste confidentiel. L'équipe Helencia examinera le contenu sous 48h.
            </p>
            <div>
              <Label className="caption-label">Raison</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="rounded-xl mt-2" data-testid="report-reason-select">
                  <SelectValue placeholder="Choisissez une raison" />
                </SelectTrigger>
                <SelectContent>
                  {REASONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption-label">Description (optionnel)</Label>
              <Textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Précisez le problème pour aider notre équipe…"
                className="rounded-xl mt-2"
                data-testid="report-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">
              Annuler
            </Button>
            <Button onClick={submit} disabled={busy} className="rounded-full bg-[#1A1A1A] hover:bg-[#2A1E12] text-white" data-testid="report-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Flag className="w-4 h-4 mr-2" />}
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
